import { formatMoney, fromMinor, type CurrencyCode, type Transfer } from '@expenditure/core';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useCurrentUser } from '../auth/AuthProvider';
import { GuideButton, useGuide } from '../components/Guide';
import { KhqrCard } from '../components/KhqrCard';
import { Sheet } from '../components/Sheet';
import { useToast } from '../components/Toast';
import { Avatar, EmptyState, ErrorState, Field, PageHeader, PageSkeleton, SectionTitle } from '../components/ui';
import { api } from '../lib/api';
import { amountPlaceholder, errorMessage, formatDate, isValidAmountInput, rateNote } from '../lib/format';
import { useBalances, useGroup, useInvalidateGroup, usePaymentRequests, usePayments } from '../lib/queries';
import { confirmDialog, haptic } from '../lib/telegram';
import type { KhqrForPayment, Member } from '../lib/types';
import { Icon } from '../components/Icon';

/** A QR on screen: either "pay this person" (with an I've-paid button) or "my QR for them to scan". */
interface QrSheetState {
  title: string;
  khqr: KhqrForPayment;
  groupAmountMinor: number;
  /** When set, the sheet offers "I've paid" to record this transfer. */
  transfer?: Transfer;
}

export function SettlePage() {
  const { groupId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const me = useCurrentUser();
  const invalidate = useInvalidateGroup();
  const balances = useBalances(groupId);
  const group = useGroup(groupId);
  const payments = usePayments(groupId);
  const requests = usePaymentRequests(groupId);
  const [busy, setBusy] = useState<string | null>(null);
  const [qrSheet, setQrSheet] = useState<QrSheetState | null>(null);
  // Settling up is where people get stuck: explain it the first time.
  const guide = useGuide(true);

  if (balances.isPending || group.isPending) return <PageSkeleton />;
  const error = balances.error ?? group.error;
  if (error || !balances.data || !group.data) {
    return (
      <ErrorState
        message={errorMessage(error, t)}
        onRetry={() => {
          void balances.refetch();
          void group.refetch();
        }}
      />
    );
  }

  const { currency, settlements, myMemberId } = balances.data;
  const { members, formerMembers, group: g, me: myMember } = group.data;
  // Marking a debt as paid is for the two people it's between, or an admin.
  const isManager = myMember.role === 'owner' || myMember.role === 'admin';
  const memberById = new Map<string, Member>([...formerMembers, ...members].map((m) => [m.id, m]));
  const nameOf = (id: string) => (id === myMemberId ? t('common.you') : (memberById.get(id)?.displayName ?? '—'));
  const plainName = (id: string) => memberById.get(id)?.displayName ?? '—';
  const keyOf = (s: Transfer) => `${s.fromMemberId}-${s.toMemberId}`;

  const recordPayment = async (transfer: Transfer, confirm = true) => {
    const amount = formatMoney(transfer.amountMinor, currency);
    if (confirm) {
      const ok = await confirmDialog(
        t('settle.confirmPaid', { from: nameOf(transfer.fromMemberId), to: nameOf(transfer.toMemberId), amount }),
      );
      if (!ok) return false;
    }
    setBusy(keyOf(transfer));
    try {
      await api.createPayment(groupId, {
        fromMemberId: transfer.fromMemberId,
        toMemberId: transfer.toMemberId,
        amount: fromMinor(transfer.amountMinor, currency),
        // Records at most what is still owed, even if it was paid meanwhile.
        settle: true,
      });
      await invalidate(groupId);
      toast(t('settle.recorded'));
      return true;
    } catch (err) {
      toast(errorMessage(err, t), 'error');
      return false;
    } finally {
      setBusy(null);
    }
  };

  /** I'm owed: have the bot DM the debtor the amount + my KHQR. */
  const requestPayment = async (transfer: Transfer) => {
    setBusy(keyOf(transfer));
    try {
      const result = await api.requestPayment(groupId, {
        fromMemberId: transfer.fromMemberId,
        amount: fromMinor(transfer.amountMinor, currency),
      });
      const name = plainName(transfer.fromMemberId);
      void invalidate(groupId);
      if (result.delivered) {
        toast(t('settle.requestSent', { name }));
        return;
      }
      const message =
        result.reason === 'GUEST'
          ? t('settle.requestGuest', { name })
          : result.reason === 'BOT_NOT_ALLOWED'
            ? t('settle.requestNotAllowed', { name })
            : t('settle.requestFailed', { name });
      toast(message, 'error');
      // Fall back to showing my QR so they can scan it in person.
      if (result.khqr) {
        setQrSheet({ title: t('settle.showQr'), khqr: result.khqr, groupAmountMinor: transfer.amountMinor });
      }
    } catch (err) {
      toast(errorMessage(err, t), 'error');
    } finally {
      setBusy(null);
    }
  };

  /** I owe: show the creditor's KHQR for the exact amount. */
  const payWithKhqr = async (transfer: Transfer) => {
    setBusy(keyOf(transfer));
    try {
      const khqr = await api.memberKhqr(groupId, transfer.toMemberId, fromMinor(transfer.amountMinor, currency));
      haptic.tap();
      setQrSheet({
        title: t('settle.payTo', { name: plainName(transfer.toMemberId) }),
        khqr,
        groupAmountMinor: transfer.amountMinor,
        transfer,
      });
    } catch (err) {
      toast(errorMessage(err, t), 'error');
    } finally {
      setBusy(null);
    }
  };

  const removePayment = async (paymentId: string) => {
    if (!(await confirmDialog(t('settle.deleteConfirm')))) return;
    try {
      await api.deletePayment(groupId, paymentId);
      await invalidate(groupId);
    } catch (err) {
      toast(errorMessage(err, t), 'error');
    }
  };

  const involvesMe = (s: Transfer) => s.fromMemberId === myMemberId || s.toMemberId === myMemberId;
  const ordered = [...settlements].sort((a, b) => Number(involvesMe(b)) - Number(involvesMe(a)));
  const owedToMe = settlements.some((s) => s.toMemberId === myMemberId);

  return (
    <main className="page page--no-tabbar app">
      <PageHeader title={t('settle.title')} action={<GuideButton onClick={guide.show} />} />
      {guide.sheet}

      {owedToMe && !me.khqr && (
        <div className="card row" style={{ background: 'var(--primary-soft)' }}>
          <span className="khqr-badge" aria-hidden>
            KHQR
          </span>
          <div className="grow small">{t('settle.addKhqrHint')}</div>
          <Link className="btn btn--primary btn--small" to="/profile">
            {t('settle.addKhqr')}
          </Link>
        </div>
      )}

      <SectionTitle>{t('settle.suggested')}</SectionTitle>
      <div className="card card--flush">
        {ordered.length === 0 ? (
          <EmptyState icon="checkCircle" title={t('settle.allSettled')} />
        ) : (
          <>
            <div className="list-sub" style={{ padding: '12px 16px 0' }}>
              {t('settle.suggestedHint')}
            </div>
            {ordered.map((s) => {
              const key = keyOf(s);
              const iOwe = s.fromMemberId === myMemberId;
              const owedMe = s.toMemberId === myMemberId;
              const creditorHasKhqr = memberById.get(s.toMemberId)?.hasKhqr ?? false;
              return (
                <div className="transfer transfer--stacked" key={key}>
                  <div className="row">
                    <Avatar name={plainName(s.fromMemberId)} photo={memberById.get(s.fromMemberId)?.profilePhoto} small />
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="list-title ellipsis">
                        {nameOf(s.fromMemberId)} <span className="transfer-arrow">→</span> {nameOf(s.toMemberId)}
                      </div>
                      <div className={`num ${owedMe ? 'pos' : iOwe ? 'neg' : 'muted'}`} style={{ fontWeight: 700 }}>
                        {formatMoney(s.amountMinor, currency)}
                      </div>
                    </div>
                  </div>
                  <div className="transfer-actions">
                    {iOwe && creditorHasKhqr && (
                      <button className="btn btn--primary btn--small" disabled={busy === key} onClick={() => void payWithKhqr(s)}>
                        {t('settle.payKhqr')}
                      </button>
                    )}
                    {owedMe && (
                      <button className="btn btn--primary btn--small" disabled={busy === key} onClick={() => void requestPayment(s)}>
                        {t('settle.request')}
                      </button>
                    )}
                    {(iOwe || owedMe || isManager) && (
                      <button className="btn btn--soft btn--small" disabled={busy === key} onClick={() => void recordPayment(s)}>
                        {t('settle.markPaid')}
                      </button>
                    )}
                  </div>
                  {!iOwe && !owedMe && !isManager && (
                    <div className="muted small">
                      {t('settle.onlyInvolved', { from: plainName(s.fromMemberId), to: plainName(s.toMemberId) })}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      <RecordPaymentForm
        groupId={groupId}
        currency={currency}
        members={members}
        myMemberId={myMemberId}
        isManager={isManager}
        suggestion={settlements.find(involvesMe)}
        onRecorded={() => void invalidate(groupId)}
      />

      {requests.data && requests.data.length > 0 && (
        <>
          <SectionTitle>{t('settle.requests')}</SectionTitle>
          <div className="card card--flush list">
            {requests.data.map((r) => {
              const mine = r.toMemberId === myMemberId;
              const status =
                r.status === 'pending'
                  ? { key: 'statusPending', tone: 'badge--primary' }
                  : r.status === 'paid'
                    ? { key: 'statusPaid', tone: 'badge--pos' }
                    : r.delivered
                      ? { key: 'statusCancelled', tone: '' }
                      : { key: 'statusUndelivered', tone: 'badge--neg' };
              return (
                <div className="list-row" key={r.id} style={{ cursor: 'default' }}>
                  <span className="icon-tile icon-tile--ink" aria-hidden>
                    <Icon name="send" size={18} />
                  </span>
                  <div className="grow">
                    <div className="list-title ellipsis">
                      {mine
                        ? t('settle.requestYouAsked', { name: plainName(r.fromMemberId) })
                        : t('settle.requestAskedYou', { name: plainName(r.toMemberId) })}
                    </div>
                    <div className="list-sub">{formatDate(r.createdAt.slice(0, 10), i18n.language)}</div>
                  </div>
                  <div className="list-end">
                    <div className="list-title num">{formatMoney(r.amountMinor, r.currency)}</div>
                    <span className={`badge ${status.tone}`}>{t(`settle.${status.key}`)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <SectionTitle>{t('settle.history')}</SectionTitle>
      <div className="card card--flush list">
        {payments.data?.length ? (
          payments.data.map((p) => (
            <div className="list-row" key={p.id} style={{ cursor: 'default' }}>
              <span className="icon-tile icon-tile--pos" aria-hidden>
                <Icon name="swap" size={20} />
              </span>
              <div className="grow">
                <div className="list-title ellipsis">
                  {nameOf(p.fromMemberId)} → {nameOf(p.toMemberId)}
                </div>
                <div className="list-sub">{formatDate(p.paymentDate, i18n.language)}</div>
              </div>
              <div className="list-end">
                <div className="list-title num">{formatMoney(p.amountMinor, p.currency)}</div>
                <button
                  className="btn btn--ghost btn--small"
                  style={{ minHeight: 24, padding: 0 }}
                  onClick={() => void removePayment(p.id)}
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="empty">{t('settle.noPayments')}</div>
        )}
      </div>

      <Sheet open={qrSheet !== null} onClose={() => setQrSheet(null)} title={qrSheet?.title ?? ''}>
        {qrSheet && (
          <div className="stack" style={{ alignItems: 'center', textAlign: 'center' }}>
            <KhqrCard khqr={qrSheet.khqr} />
            {qrSheet.khqr.currency !== (currency as CurrencyCode) && (
              <div className="small muted num">
                {formatMoney(qrSheet.groupAmountMinor, currency)} ·{' '}
                {t('settle.qrConverted', {
                  amount: formatMoney(qrSheet.khqr.amountMinor, qrSheet.khqr.currency),
                  rate: rateNote([currency, qrSheet.khqr.currency], g.rates),
                })}
              </div>
            )}
            <div className="muted small">{t('settle.scanToPay')}</div>
            {qrSheet.transfer && (
              <button
                className="btn btn--primary btn--block"
                onClick={() =>
                  void recordPayment(qrSheet.transfer!, false).then((ok) => {
                    if (ok) setQrSheet(null);
                  })
                }
              >
                <Icon name="check" size={18} />
                {t('settle.iPaid')}
              </button>
            )}
          </div>
        )}
      </Sheet>
    </main>
  );
}

/** Any amount between any two members, e.g. a partial repayment or cash. */
function RecordPaymentForm(props: {
  groupId: string;
  currency: CurrencyCode;
  members: Member[];
  myMemberId: string;
  /** Admins may record a payment between any two members; others only their own. */
  isManager: boolean;
  suggestion?: Transfer;
  onRecorded: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { members, currency } = props;
  const [open, setOpen] = useState(false);
  const [from, setFromRaw] = useState(props.suggestion?.fromMemberId ?? props.myMemberId);
  const [to, setToRaw] = useState(props.suggestion?.toMemberId ?? members.find((m) => m.id !== props.myMemberId)?.id ?? '');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const mine = from === props.myMemberId || to === props.myMemberId;
  const valid = from !== to && Boolean(to) && (mine || props.isManager) && isValidAmountInput(amount, currency);
  // Not an admin: one side is always you, so picking someone else on one
  // side puts you on the other.
  const setFrom = (id: string) => {
    setFromRaw(id);
    if (!props.isManager && id !== props.myMemberId) setToRaw(props.myMemberId);
  };
  const setTo = (id: string) => {
    setToRaw(id);
    if (!props.isManager && id !== props.myMemberId) setFromRaw(props.myMemberId);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await api.createPayment(props.groupId, { fromMemberId: from, toMemberId: to, amount: amount.trim() });
      toast(t('settle.recorded'));
      setAmount('');
      setOpen(false);
      props.onRecorded();
    } catch (error) {
      toast(errorMessage(error, t), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openForm = () => {
    // Start from the current suggestion; it may have changed since mount.
    setFromRaw(props.suggestion?.fromMemberId ?? props.myMemberId);
    setToRaw(props.suggestion?.toMemberId ?? members.find((m) => m.id !== props.myMemberId)?.id ?? '');
    setAmount('');
    setOpen(true);
  };

  if (!open) {
    return (
      <button className="btn btn--soft btn--block" onClick={openForm}>
        <Icon name="plus" size={18} />
        {t('settle.recordPayment')}
      </button>
    );
  }

  const label = (m: Member) => (m.id === props.myMemberId ? `${m.displayName} (${t('common.you')})` : m.displayName);
  return (
    <form className="card stack" onSubmit={(e) => void submit(e)}>
      <div>
        <div className="list-title">{t('settle.recordPayment')}</div>
        <div className="muted small">{props.isManager ? t('settle.recordHint') : t('settle.recordHintMine')}</div>
      </div>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <Field label={t('settle.from')}>
          <select className="input" value={from} onChange={(e) => setFrom(e.target.value)}>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {label(m)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('settle.to')}>
          <select className="input" value={to} onChange={(e) => setTo(e.target.value)}>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {label(m)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={`${t('settle.amount')} (${currency})`}>
        <input
          className="input num"
          inputMode="decimal"
          placeholder={amountPlaceholder(currency)}
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(',', '.').replace(/[^\d.]/g, ''))}
        />
      </Field>
      <div className="row">
        <button type="button" className="btn btn--ghost grow" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </button>
        <button className="btn btn--primary grow" disabled={!valid || submitting}>
          {t('settle.record')}
        </button>
      </div>
    </form>
  );
}
